import React, { useState } from 'react';
import { MARKET_TABS, DEFAULT_PERIOD, getTab } from './config';
import { useMarketData } from './useMarketData';
import PeriodSelector from './shared/PeriodSelector';
import DataSources from './shared/DataSources';
import OverviewTab from './tabs/OverviewTab';
import FedFundsTab from './tabs/FedFundsTab';
import CpiTab from './tabs/CpiTab';
import UnemploymentTab from './tabs/UnemploymentTab';
import TreasuryTab from './tabs/TreasuryTab';
import MbsSpreadTab from './tabs/MbsSpreadTab';
import QtPressureTab from './tabs/QtPressureTab';
import GeopoliticalTab from './tabs/GeopoliticalTab';

const TAB_COMPONENTS = {
  overview: OverviewTab,
  fedfunds: FedFundsTab,
  cpi: CpiTab,
  unemployment: UnemploymentTab,
  treasury: TreasuryTab,
  mbs: MbsSpreadTab,
  qt: QtPressureTab,
  geopolitical: GeopoliticalTab,
};

/**
 * Market Data page shell: owns the two pieces of view state (active tab and
 * comparison period), fetches once via useMarketData, and hands the result to
 * whichever tab is active.
 *
 * Tabs are separate files because they are meant to look different. The period
 * is a value rather than a set of files because it only changes which data is
 * fetched - the two are independent axes.
 */
const MarketData = ({ onBackToSimulator }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState(DEFAULT_PERIOD);

  const data = useMarketData(period);
  const ActiveTab = TAB_COMPONENTS[activeTab] || OverviewTab;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-start justify-between gap-4">
            <button
              onClick={onBackToSimulator}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 shrink-0"
            >
              <span>←</span>
              <span>Back to Simulator</span>
            </button>

            <div className="text-center flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Live Market Data</h1>
              {data.lastUpdated && (
                <p className="text-sm text-gray-500">
                  Last updated: {data.lastUpdated.toLocaleTimeString()}
                </p>
              )}
              <PeriodSelector value={period} onChange={setPeriod} />
            </div>

            <button
              onClick={data.refresh}
              disabled={data.loading}
              className="flex items-center space-x-2 px-3 py-2 text-sm bg-blue-100 hover:bg-blue-200 rounded-md text-blue-700 transition-colors disabled:opacity-50 shrink-0"
            >
              <span className={data.loading ? 'animate-spin' : ''}>🔄</span>
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </header>

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {MARKET_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                title={tab.description}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                <span className="text-lg">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <ActiveTab data={data} period={period} tab={getTab(activeTab)} />
        <DataSources marketData={data.marketData} />
      </div>
    </div>
  );
};

export default MarketData;
